import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import { Alert, Typography } from '@mui/material';
import axios from "axios";
import { IsLoginContext } from "../../components/IsLoginContext";
import * as common from '../../commons/common';
import uniLogo from '../../images/UNI_LOGO_BLUE_SVG.svg';

export default function Login() {
    const {setUser, setIsLogin } = useContext(IsLoginContext);
    const navigate = useNavigate();

    const [error, setError] = useState('');
    const [inputId, setInputId] = useState('');
    const [inputPw, setInputPw] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const baseUrl = common.getApiUrl();

    // 로그인 처리
    const handleLogin = async (event) => {
        event.preventDefault();

        if (!inputId.trim() || !inputPw.trim()) {
            setError('아이디와 비밀번호를 입력해주세요.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post(`${baseUrl}/auth/login`, {
                id: inputId,
                password: inputPw
            });

            const { access_token, refresh_token, role, memberId } = response.data;

            // 기존 데이터 완전히 제거 (중요!)
            localStorage.clear();

            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token);
            localStorage.setItem('role', role);
            localStorage.setItem('memberId', memberId);
            setIsLogin(true);

            fetchMemberName(access_token, role, memberId);
        } catch (error) {
            setError(`${error.response ? error.response.data.message : error.message}`);
            setIsLoading(false);
        }
    };

    // 사용자 이름 localStorage에 저장하고 리다이렉션
    const fetchMemberName = async (accessToken, role, memberId) => {
        try {
            const response = await axios.get(`${baseUrl}/member/${inputId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });

            const { memberNm } = response.data;
            localStorage.setItem('memberNm', memberNm);

            // 컨텍스트에 완전한 사용자 정보 설정
            setUser({
                memberId,
                memberNm,
                role
            });

            console.log('로그인 성공 및 컨텍스트 업데이트:', { memberId, memberNm, role });

            // 사용자 리다이렉션
            redirectUser(role);
        } catch (error) {
            console.error('Error fetching member name:', error);
            // 이름을 가져오지 못해도 기본 정보로 설정
            setUser({
                memberId,
                memberNm: '',
                role
            });
            redirectUser(role);
        }

        setIsLoading(false);
    };

    const redirectUser = (role) => {
        if (role === "CHECKER") {
            navigate('/checker');
        } else if (role === "ADMIN") {
            navigate('/admin');
        } else if (role === "MANAGE" || role === "MANAGER") {
            navigate('/manager');
        } else {
            setError(`알 수 없는 사용자 역할입니다: ${role}`);
        }
    };

    const handleInputId = (e) => {
        setError('');
        setInputId(e.target.value);
    };

    const handleInputPw = (e) => {
        setError('');
        setInputPw(e.target.value);
    };

    // 엔터 키 처리
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleLogin(e);
        }
    };

    return (
        <>
            <Box sx={{ height: '100vh', bgcolor: 'var(--main-white-color)' }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'coulumn', justifyContent: 'center', alignItems: 'center' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: { xs: 'calc(100% - 60px)', sm: '730px' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: { xs: '100%', sm: '50%' }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Box component="img"
                                         src={uniLogo}
                                         sx={{
                                             maxWidth: { xs: '130px', sm: '155px' },
                                             marginBottom: '30px'
                                         }}>
                                    </Box>
                                </Box>
                                <Typography>유니체크에 오신것을 환영합니다 !</Typography>
                                <Box component="form" onSubmit={handleLogin} noValidate autoComplete="off"
                                     sx={{ width: '100%' }}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        id="username"
                                        name="username"
                                        label="아이디"
                                        autoComplete="off"
                                        autoFocus
                                        onChange={handleInputId}
                                        onKeyPress={handleKeyPress}
                                        value={inputId || ''}
                                        disabled={isLoading}
                                        sx={{
                                            marginBottom: '0px',
                                            backgroundColor: 'white',
                                            borderRadius: '4px',
                                        }}
                                    />
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        name="password"
                                        type="password"
                                        id="password"
                                        label="비밀번호"
                                        autoComplete="off"
                                        onChange={handleInputPw}
                                        onKeyPress={handleKeyPress}
                                        value={inputPw || ''}
                                        disabled={isLoading}
                                        sx={{
                                            backgroundColor: 'white',
                                            borderRadius: '10px',
                                        }}
                                    />
                                    {error && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {error}
                                        </Alert>
                                    )}
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={isLoading}
                                        sx={{
                                            fontSize: '1.5rem',
                                            marginTop: '10px',
                                            height: { xs: '56px', sm: '56px' },
                                            backgroundColor: 'var(--main-blue-color)',
                                            '&:hover': { backgroundColor: 'var(--main-blue-color)' },
                                            '&:disabled': {
                                                backgroundColor: '#cccccc',
                                            }
                                        }}
                                    >
                                        {isLoading ? '로그인 중...' : '로그인'}
                                    </Button>
                                </Box>
                                <Link href="/sign-up" sx={{ alignSelf: 'flex-start', marginTop: '15px', fontSize: '12px', color: '#000', textDecoration: 'underline' }}>
                                    {"회원가입"}
                                </Link>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}